import { useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useRef, useState } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";

import { GET_MODULE_LOGS, GET_STACK_LOGS, ModuleLogsData, StackLogsData } from "./gql";
import { getEntityLogs, getTimestamp } from "./helpers";

type EntityLogs = NonNullable<ReturnType<typeof getEntityLogs>>;

type GetRunLogsOptions = {
  until: number | null;
  state: string;
  stateVersion?: number | null;
  isModuleRun: boolean;
  runId: string;
  stackId: string;
};

export const useGetRunLogs = ({
  until,
  state,
  stateVersion,
  isModuleRun,
  runId,
  stackId,
}: GetRunLogsOptions) => {
  const [backOff, setBackOff] = useState(1);
  const [token, setToken] = useState<string | null>(null);
  const [fetchMoreAt, setFetchMoreAt] = useState<number | null>(null);

  const { onError } = useTypedContext(FlashContext);

  const expectStragglers = useCallback(() => {
    // The phase is not finished yet so we can expect more logs.
    if (until === null) {
      return true;
    }

    // Expect stragglers for up to 5 seconds.
    return getTimestamp() - until < 5;
  }, [until]);

  // Tracks which query result we've already scheduled a fetch for, so the reactive effect
  // below doesn't double-schedule a fetch already handled by the fetchMore() resolution
  // path (see comment there for why that path exists at all).
  const scheduledDataRef = useRef<StackLogsData | ModuleLogsData | undefined>(undefined);

  // Marks that the fetchMore() resolution path below already scheduled the next fetch for
  // the settlement currently reflected in `data`, so the reactive effect's very next run
  // skips instead of scheduling a second time for the same round. Can't rely on
  // scheduledDataRef alone for this: Apollo's reactive `data` after a fetchMore() cache
  // write is not reference-equal to that same call's own resolved value, even though they
  // describe the same settlement - the flag covers that gap.
  const handledByFetchMoreRef = useRef(false);

  const scheduleNext = useCallback(
    (logs: EntityLogs, prevToken: string | null, prevBackOff: number) => {
      const { finished, hasMore, nextToken } = logs;

      if (finished && !expectStragglers()) {
        setFetchMoreAt(null);
        return;
      }

      // Reset back-off if new data is available, otherwise bump it by one.
      const nextBackOff = prevToken !== nextToken ? 1 : prevBackOff + 1;

      setBackOff(nextBackOff);
      setToken(nextToken);

      // Ensure maximum 5 seconds between checks.
      const nextFetchInMs = Math.min(5000, hasMore ? 300 : 900 * nextBackOff);

      setFetchMoreAt(getTimestamp() + nextFetchInMs);
    },
    [expectStragglers]
  );

  const { data, error, fetchMore, loading } = useQuery(
    isModuleRun ? GET_MODULE_LOGS : GET_STACK_LOGS,
    {
      variables: {
        [isModuleRun ? "moduleId" : "stackId"]: stackId,
        runId,
        state,
        stateVersion,
      },
    }
  );

  // Covers the initial fetch and any refetch triggered by variables changing (e.g. state
  // transitioning). Two separate guards against double-scheduling, for two separate causes:
  // - scheduledDataRef stops this effect from re-scheduling for its OWN token/backOff state
  //   updates re-running it (both are in the deps below) while `data` hasn't actually changed.
  // - handledByFetchMoreRef stops it from re-scheduling a round the fetchMore path below
  //   already scheduled - that resolution's `data` isn't reference-equal to what shows up here
  //   afterwards, so scheduledDataRef alone can't catch that case.
  useEffect(() => {
    if (handledByFetchMoreRef.current) {
      handledByFetchMoreRef.current = false;
      scheduledDataRef.current = data;
      return;
    }

    if (!data || data === scheduledDataRef.current) {
      return;
    }

    const logs = getEntityLogs(data);

    if (!logs) {
      return;
    }

    scheduledDataRef.current = data;
    // Synchronizes local polling state with the just-settled query, not deriving one piece
    // of state from another - a legitimate use of an effect the rule can't distinguish here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    scheduleNext(logs, token, backOff);
  }, [data, scheduleNext, token, backOff]);

  useEffect(() => {
    let timeoutId: number | null = null;

    if (fetchMoreAt !== null) {
      timeoutId = window.setTimeout(async () => {
        try {
          const result = await fetchMore({
            variables: { token },
            updateQuery: (current, { fetchMoreResult }) => {
              if (!fetchMoreResult) return current;

              const currentLogs = getEntityLogs(current);
              const newLogs = getEntityLogs(fetchMoreResult);

              if (currentLogs && newLogs) {
                newLogs.messages =
                  currentLogs.nextToken === newLogs.nextToken
                    ? currentLogs.messages
                    : currentLogs.messages.concat(newLogs.messages);
              }

              return fetchMoreResult;
            },
          });

          // Read straight off this call's resolved value rather than the reactive `data`
          // above: back-to-back polls that return unchanged logs can settle with the same
          // `data` reference, which would silently stop the reactive effect from re-firing.
          const logs = getEntityLogs(result.data);

          if (logs) {
            handledByFetchMoreRef.current = true;
            scheduleNext(logs, token, backOff);
          }
        } catch (err) {
          onError(err);
        }
      }, fetchMoreAt - getTimestamp());
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [fetchMore, fetchMoreAt, token, backOff, onError, scheduleNext]);

  const runLogs = getEntityLogs(data);

  return {
    runLogs,
    loading,
    error,
  };
};
